import { Field, Input, Text } from "@chakra-ui/react";
import { InputGroup } from "../ui/input-group";
import { ChangeEvent, KeyboardEvent, ReactNode } from "react";
import { AppColors } from "@/styles/colors";
import { Control, FieldValues, useController } from "react-hook-form";

interface Props {
  startElement?: ReactNode;
  inputName: string;
  placeholder?: string;
  labelText?: string;
  disabled?: boolean;
  height?: string;
  minHeight?: string;
  required?: boolean;
  maxLength?: number;
  control?: Control<FieldValues>;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  endElement?: ReactNode;
  defaultValue?: any;
  allowedPattern?: RegExp;
  type?: string;
  minTime?: string;
  maxTime?: string;
  maxW?: string;
}

export const InputField = ({
  inputName,
  labelText,
  startElement,
  endElement,
  placeholder,
  disabled,
  height,
  minHeight,
  required,
  maxLength,
  control,
  defaultValue,
  allowedPattern,
  type,
  maxTime,
  minTime,
  maxW,
  onChange,
}: Props) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name: inputName,
    control: control,
    defaultValue,
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (allowedPattern) {
      const key = e.key;
      if (!allowedPattern.test(key) && key.length === 1) {
        e.preventDefault(); // Evita que se escriba el carácter
      }
    }
  };

  return (
    <Field.Root invalid={!!error} required={required}>
      {labelText && (
        <Field.Label>
          <Text fontWeight={600}>{labelText}</Text>
          {required && (
            <Text fontWeight={600} color={AppColors.REQUIRED}>
              *
            </Text>
          )}
        </Field.Label>
      )}
      <InputGroup
        width="100%"
        flex="1"
        startElement={startElement}
        endElement={endElement}
        _placeholder={{
          color: AppColors.LIGHT_GRAY_TEXT,
        }}
      >
        <Input
          value={field.value}
          placeholder={placeholder}
          height={height}
          minH={minHeight}
          defaultValue={defaultValue}
          fontSize="14px"
          paddingX="12px"
          disabled={disabled}
          maxLength={maxLength}
          maxW={maxW}
          data-testid="input-component"
          onChange={(e) => {
            onChange?.(e);
            field.onChange(e);
          }}
          type={type}
          min={minTime}
          max={maxTime}
          onKeyDown={handleKeyDown}
        />
      </InputGroup>
      {error && (
        <Field.ErrorText data-testid="input-error-text">
          <Text
            css={{
              "@media (max-width: 1350px) and (min-width: 1272px)": {
                fontSize: "12px",
              },
              "@media (max-width: 1590px) and (min-width: 1351px)": {
                fontSize: "14px",
              },
              "@media (min-width: 1591px)": {
                fontSize: "16px",
              },
            }}
          >
            {String(error?.message)}
          </Text>
        </Field.ErrorText>
      )}
    </Field.Root>
  );
};
